import { enUS, ptBR, pt, pl, de } from 'date-fns/locale';
import i18n from '@/i18n/config';

/**
 * Get the date-fns locale based on current i18n language
 * This ensures date formatting (months, days) are translated
 */
export function getDateLocale() {
  const lang = i18n.language;
  switch (lang) {
    case 'pt-BR':
      return ptBR;
    case 'pt-PT':
      return pt;
    case 'de':
      return de;
    case 'pl':
      return pl;
    default:
      return enUS;
  }
}

/**
 * Get locale-aware date format patterns
 * Portuguese uses day/month/year, English uses month/day/year
 */
export function getDateFormats() {
  const lang = i18n.language;

  if (lang === 'pl') {
    return {
      // Full date with weekday: "poniedziałek, 15 stycznia 2024"
      fullWithWeekday: 'EEEE, d MMMM yyyy',
      // Full date: "15 stycznia 2024"
      full: 'd MMMM yyyy',
      // Medium date: "15 sty 2024"
      medium: 'd MMM yyyy',
      // Short date: "15.01.2024"
      short: 'dd.MM.yyyy',
      // Month and year: "styczeń 2024"
      monthYear: 'MMMM yyyy',
      // Day and month: "15 sty"
      dayMonth: 'd MMM',
      // Day and month short: "15.01"
      dayMonthShort: 'dd.MM',
      // Weekday short and day: "pon 15"
      weekdayDay: 'EEE d',
      // Full weekday and day: "poniedziałek, 15"
      weekdayDayFull: 'EEEE, d',
      // Date range separator
      rangeSeparator: ' – ',
    };
  }

  if (lang === 'de') {
    return {
      // Full date with weekday: "Montag, 15. Januar 2024"
      fullWithWeekday: 'EEEE, d. MMMM yyyy',
      // Full date: "15. Januar 2024"
      full: 'd. MMMM yyyy',
      // Medium date: "15. Jan. 2024"
      medium: 'd. MMM yyyy',
      // Short date: "15.01.2024"
      short: 'dd.MM.yyyy',
      // Month and year: "Januar 2024"
      monthYear: 'MMMM yyyy',
      // Day and month: "15. Jan."
      dayMonth: 'd. MMM',
      // Day and month short: "15.01."
      dayMonthShort: 'dd.MM.',
      // Weekday short and day: "Mo. 15."
      weekdayDay: 'EEE d.',
      // Full weekday and day: "Montag, 15."
      weekdayDayFull: 'EEEE, d.',
      // Date range separator
      rangeSeparator: ' – ',
    };
  }

  if (lang === 'pt-BR' || lang === 'pt-PT') {
    return {
      // Full date with weekday: "segunda-feira, 15 de janeiro de 2024"
      fullWithWeekday: "EEEE, d 'de' MMMM 'de' yyyy",
      // Full date: "15 de janeiro de 2024"
      full: "d 'de' MMMM 'de' yyyy",
      // Medium date: "15 jan 2024"
      medium: "d MMM yyyy",
      // Short date: "15/01/2024"
      short: 'dd/MM/yyyy',
      // Month and year: "janeiro de 2024"
      monthYear: "MMMM 'de' yyyy",
      // Day and month: "15 jan"
      dayMonth: 'd MMM',
      // Day and month short: "15/01"
      dayMonthShort: 'dd/MM',
      // Weekday short and day: "seg 15"
      weekdayDay: 'EEE d',
      // Full weekday and day: "segunda-feira, 15"
      weekdayDayFull: 'EEEE, d',
      // Date range separator
      rangeSeparator: ' - ',
    };
  }

  // English (default)
  return {
    // Full date with weekday: "Monday, January 15, 2024"
    fullWithWeekday: 'EEEE, MMMM d, yyyy',
    // Full date: "January 15, 2024"
    full: 'MMMM d, yyyy',
    // Medium date: "Jan 15, 2024"
    medium: 'MMM d, yyyy',
    // Short date: "01/15/2024"
    short: 'MM/dd/yyyy',
    // Month and year: "January 2024"
    monthYear: 'MMMM yyyy',
    // Day and month: "Jan 15"
    dayMonth: 'MMM d',
    // Day and month short: "01/15"
    dayMonthShort: 'MM/dd',
    // Weekday short and day: "Mon 15"
    weekdayDay: 'EEE d',
    // Full weekday and day: "Monday, 15"
    weekdayDayFull: 'EEEE, d',
    // Date range separator
    rangeSeparator: ' - ',
  };
}
